import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { Panel } from '../../music24/components/primitives/Panel';
import { useChecklistState } from './useChecklistState';
import { useChecklistAction } from './useChecklistAction';
import { useChecklistAnalytics } from './useChecklistAnalytics';
import { useChecklistNudge } from './useChecklistNudge';
import { useChecklistVisibility } from './useChecklistVisibility';
import { useFirstReleaseSummary } from './useFirstReleaseSummary';
import { firstReleaseAccountId } from './firstReleaseAccountId';
import { useTrialDaysLeft } from './useTrialDaysLeft';
import { ChecklistProgress } from './ChecklistProgress';
import { ChecklistNextStep } from './ChecklistNextStep';
import { ChecklistItemRow } from './ChecklistItemRow';
import { ChecklistDone } from './ChecklistDone';
import { FIRST_RELEASE_CHECKLIST_ID } from './scrollToFirstReleaseChecklist';
import { checklistMessages as messages } from './checklistMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the checklist; each part uses its own slice of it */
import styles from './FirstReleaseChecklist.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The "Your first release" card at the top of Home (design: onboarding d1-d4, d7). It shows the
 * checklist of the active artist: each artist has its own. The server computes the list
 * (`onboarding.state.checklist`); it is null for a member, for a workspace with
 * no artist and after a dismiss, and then the card renders nothing. The card leads with the first
 * item that is not done; the whole list sits next to it.
 */
const FirstReleaseChecklistComponent: React.FC = () => {
  const intl = useIntl();
  const { state, accountId } = useChecklistState();
  const checklist = state?.checklist ?? null;
  const summary = useFirstReleaseSummary(firstReleaseAccountId(state), state?.firstReleaseUuid);
  const trialDaysLeft = useTrialDaysLeft();
  const runAction = useChecklistAction();
  const runNudge = useChecklistNudge();
  const { dismiss } = useChecklistVisibility(accountId);
  // The hidden flag belongs to one artist: a switch to another artist shows its own card.
  const [hiddenFor, setHiddenFor] = useState<number | null>(null);
  const isHidden = hiddenFor !== null && hiddenFor === accountId;
  useChecklistAnalytics(state);

  const hide = useCallback(() => {
    setHiddenFor(accountId);
    dismiss();
  }, [accountId, dismiss]);

  if (checklist === null || isHidden) {
    return null;
  }
  if (checklist.complete) {
    return <ChecklistDone songTitle={summary.title} onHide={hide} />;
  }

  const nextIndex = checklist.items.findIndex((item) => !item.done);
  const nextItem = checklist.items[nextIndex];
  const counts = { done: checklist.doneCount, total: checklist.total };

  return (
    <Panel
      as="section"
      className={styles.card}
      aria-label={intl.formatMessage(messages.title)}
      // design: navigation-v2 6c. The sidebar row scrolls here and moves the focus here.
      id={FIRST_RELEASE_CHECKLIST_ID}
      tabIndex={-1}
      data-testid="first-release-checklist"
    >
      <div className={styles.head}>
        <ChecklistProgress done={counts.done} total={counts.total} />
        <div className={styles.headText}>
          <b>{intl.formatMessage(messages.title)}</b>
          <small data-testid="checklist-sub">
            {summary.title === null
              ? intl.formatMessage(messages.subNoSong, counts)
              : intl.formatMessage(messages.sub, { ...counts, song: summary.title, when: summary.when })}
          </small>
        </div>
        <button type="button" className={styles.linkButton} onClick={hide}>
          {intl.formatMessage(messages.dismiss)}
        </button>
      </div>
      <div className={styles.body}>
        {nextItem !== undefined && (
          <ChecklistNextStep
            item={nextItem}
            readyAssets={summary.readyAssets}
            trialDaysLeft={trialDaysLeft}
            onAction={runAction}
          />
        )}
        <ul className={styles.items}>
          {checklist.items.map((item, index) => (
            <ChecklistItemRow
              key={item.key}
              item={item}
              isNext={index === nextIndex}
              variant="compact"
              readyAssets={summary.readyAssets}
              onAction={runAction}
              onNudge={runNudge}
            />
          ))}
        </ul>
      </div>
    </Panel>
  );
};

export const FirstReleaseChecklist = TypedMemo(FirstReleaseChecklistComponent);
